import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { changeMapCenter, setMapZoom } from "../../redux/actions/shared-actions";
import { setNavTram, setNavTrolley, setNavBus } from "../../redux/actions/navigation-actions";

/**
 * Autocomplete text input used in the Drawer component.
 * @component
 * @category Components
 * @subcategory Navigation
 * @param {string} label  determines the mui autocomplete options data
 */

const StopsAutocomplete = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const useStyles = makeStyles(() => ({
    inputRoot: {
      '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
        paddingLeft: 26,
        color: "#fff",
      },
      "& .MuiOutlinedInput-root": {
        color: "#fff",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#fff",
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#fff",
        borderWidth: "2px",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#fff",
        borderWidth: "2px",
      },
    },
    listbox: {
      backgroundColor: "#2c54a4",
      border: "2px solid white",
      borderRadius: "5px",
      fontFamily: '"Lucida Console", "Courier New", "monospace"',
      color: "white",
      '& .MuiAutocomplete-option[aria-selected="true"]': {
        // works
        backgroundColor: "#00000040",
      },
      '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
        // works
        backgroundColor: "#00000040",
      },
      '& .MuiAutocomplete-option[aria-selected="false"].Mui-focused': {
        // works
        backgroundColor: "#00000040",
      },
    },
    paper: {
      marginTop: "10px",
    },
    noOptions: {
      backgroundColor: "#2c54a4",
      border: "2px solid white",
      borderRadius: "5px",
      fontFamily: '"Lucida Console", "Courier New", "monospace"',
      color: "white",
    },
  }));
  const classes = useStyles();
  const { selectedLine, tramsData, trolleysData, busesData, navigation, isDrawerOpen } = useSelector((state) => ({
    selectedLine: state.shared.selectedLine,
    tramsData: state.staticData.filteredTMData,
    trolleysData: state.staticData.filteredTBData,
    busesData: state.staticData.filteredBData,
    navigation: {
      isTramActive: state.navigation.tram.isActive,
      isTrolleyActive: state.navigation.trolley.isActive,
      isBusActive: state.navigation.bus.isActive,
    },
    isDrawerOpen: state.shared.isDrawerOpen,
  }));
  const navigationDataMapper = {
    isTramActive: tramsData,
    isBusActive: busesData,
    isTrolleyActive: trolleysData,
  };
  const [inputValue, setInputValue] = React.useState(null);
  const [autocompleteOptions, setAutocompleteOptions] = React.useState({});

  React.useEffect(() => {
    setInputValue(null);
  }, [selectedLine, isDrawerOpen]);

  const extractStops = (data) => {
    const stops = {};
    data.forEach((line) => {
      Object.entries(line).forEach(([key, value]) => {
        if (key.includes("direction")) {
          value.stops.forEach((stop) => {
            const stopId = stop.id.slice(stop.id.length - 4);
            if (!stops[stopId]) {
              stops[`${stopId}: ${stop.name}`] = stop;
            }
          });
        }
      });
    });
    return stops;
  };

  React.useEffect(() => {
    const stops = [];
    Object.entries(navigation).forEach(([vehicleType, isActive]) => {
      if (isActive) {
        navigationDataMapper[vehicleType].forEach((line) => stops.push(line));
      }
    });
    setAutocompleteOptions(extractStops(stops));
    // eslint-disable-next-line
  }, [tramsData, trolleysData, busesData, navigation.isTramActive, navigation.isBusActive, navigation.isTrolleyActive]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <br />
      <Autocomplete
        classes={classes}
        className='stops-autocomplete'
        disablePortal
        value={inputValue}
        disableClearable
        id='stops-autocomplete'
        noOptionsText={t("NO_OPTIONS")}
        options={Object.keys(autocompleteOptions)}
        onChange={(event, value) => {
          setInputValue(value);
          if (value) {
            if ((!selectedLine && !navigation.isTramActive) || navigation.isTrolleyActive || navigation.isBusActive) {
              if (autocompleteOptions[value].id.includes("TB")) {
                dispatch(setNavTrolley(true));
              }
              if (autocompleteOptions[value].id.includes("TM") && !autocompleteOptions[value].id.includes("A")) {
                dispatch(setNavTram(true));
              }
              if (autocompleteOptions[value].id.includes("A")) {
                dispatch(setNavBus(true));
              }
            }
            dispatch(changeMapCenter([autocompleteOptions[value].latitude, autocompleteOptions[value].longitude]));
            dispatch(setMapZoom(18));
          }
        }}
        sx={{ width: 270 }}
        renderInput={(params) => <TextField {...params} label={t("STOPS")} />}
      />
      <br />
    </div>
  );
};
export default React.memo(StopsAutocomplete);
